import { Injectable } from "@angular/core";
import { Document } from "@models/documents/document";
import { Trust } from "@models/trusts/trust";
import { FileCreateDto } from "@models/ui-models/files";
import BaseApiService from "@shared/services/base/base-api.service";
import { chunkArray } from "@shared/utils/chunk-array";
import { filterPatchOperations } from "@shared/utils/filter-patch-operations";
import { generate, Observer, Operation } from "fast-json-patch";
import { concatMap, delay, forkJoin, from, map, Observable, of, toArray } from "rxjs";

@Injectable({ providedIn: 'root' })
export class TrustsApiService extends BaseApiService {
  list(): Observable<Trust[]> {
    return this.api.get<Trust[]>(`${ this.apiUrl }/trusts`);
  }

  getTrustProfile(id: string, applyIncompleteDocuments = true, excludedDocumentId = ''): Observable<Trust> {
    const options = { params: { excludedDocumentId, applyIncompleteDocuments } };
    return this.api.get<Trust>(`${ this.apiUrl }/trusts/${ id }`, options)
      .pipe(map((data) => new Trust(data)));
  }

  addTrust(trust: Trust): Observable<Trust> {
    return this.api.post<Trust>(`${ this.apiUrl }/trusts`, trust.prepareToRequest());
  }

  patchTrust(id: string, observer: Observer<Trust>): Observable<Trust> {
    const filteredPatchOperations: Operation[] = observer ? filterPatchOperations(generate(observer)) : [];
    return this.api.patch<Trust>(`${ this.apiUrl }/trusts/${ id }`, filteredPatchOperations, { headers: { 'Content-Type': 'application/json-patch+json' } })
      .pipe(map((data) => new Trust(data)));
  }

  patchOperationTrust(entityId: string, patchOperations: Operation[]): Observable<Trust> {
    const filteredPatchOperations: Operation[] = patchOperations.length ? filterPatchOperations(patchOperations) : [];
    return this.api.patch<Trust>(`${ this.apiUrl }/trusts/${ entityId }`, filteredPatchOperations, { headers: { 'Content-Type': 'application/json-patch+json' } })
      .pipe(map((data) => new Trust(data)));
  }

  bulkPatchOperationTrust(createdDocuments: {
    entityId: string
  }[], patchOperations: Operation[]): Observable<(Trust | null)[]> {
    const batches = chunkArray(createdDocuments, 10);

    return from(batches).pipe(
      concatMap(batch =>
        forkJoin(batch.map(doc => this.patchOperationTrust(doc.entityId, patchOperations))).pipe(
          delay(1000)
        )
      ),
      toArray(),
      concatMap(responses => of(...responses).pipe(concatMap(res => res), toArray()))
    );
  }

  applyChanges(document: Document): Observable<Trust> {
    return this.api.post<Trust>(`${ this.apiUrl }/trusts/apply-changes`, Document.prepareToRequest(document));
  }

  analysePdf(body: FileCreateDto): Observable<Trust> {
    return this.api.post<Trust>(`${ this.apiUrl }/trusts/analyse/pdf`, body);
  }
}
